/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Dialog, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import FinancialAidApprovedModal from 'bundles/finaid-common/components/FinancialAidApprovedModal';
import { APPROVAL_BUFFER_MILLI_SECS } from 'bundles/finaid-common/constants/limits';
import { MODAL_TYPES } from 'bundles/finaid-common/constants/openAidModalTypes';
import { FindAidState as APPLICATION_STATE } from 'bundles/finaid-common/enhancers/types';
import type { FinancialAidApplicationStateType } from 'bundles/finaid-common/enhancers/types';

import _t from 'i18n!nls/finaid';

// Utility function to get the appropriate course type text
const getProgramTypeText = (type: keyof typeof MODAL_TYPES | null): string => {
  switch (type) {
    case MODAL_TYPES.COURSE:
      return _t('course');
    case MODAL_TYPES.SPECIALIZATION:
      return _t('Specialization');
    case MODAL_TYPES.PROFESSIONAL_CERTIFICATE:
      return _t('Professional Certificate');
    default:
      return _t('course');
  }
};

export type Props = {
  handleClose: () => void;
  handlePreview?: () => void;
  handleCancelAndEnroll: () => void;
  type: keyof typeof MODAL_TYPES | null;
  approvalDate?: number | null;
  applicationState?: FinancialAidApplicationStateType | null;
  cartId?: number;
  isPreviewAvailable?: boolean;
  isPreviewLoading?: boolean;
};
const OpenAidApplicationModalV2 = ({
  handleClose,
  type,
  approvalDate,
  applicationState,
  cartId,
  handlePreview,
  isPreviewLoading,
  isPreviewAvailable,
  handleCancelAndEnroll,
}: Props) => {
  const isXsDown = useMediaQuery(breakpoints.down('xs'));
  const programTypeText = getProgramTypeText(type);
  const { HeadingGroup, Content, Actions } = Dialog;

  if (applicationState === APPLICATION_STATE.PaymentPending && cartId) {
    return <FinancialAidApprovedModal productType={type} cartId={cartId} handleClose={handleClose} />;
  }
  return (
    <Dialog open={true} onClose={handleClose}>
      <HeadingGroup>
        {_t('You have an open financial aid request for this #{programType}', { programType: programTypeText })}
      </HeadingGroup>

      <Content id="dialog-content">
        {!!approvalDate && (
          <Typography2
            component="p"
            variant="bodyPrimary"
            css={css`
              margin-bottom: var(--cds-spacing-300);
            `}
          >
            <FormattedMessage
              message={_t('Your request is currently being processed and you should hear back by {approvalDate}.')}
              programType={programTypeText}
              approvalDate={
                <strong>
                  {formatDateTimeDisplay(approvalDate + APPROVAL_BUFFER_MILLI_SECS, LONG_DATE_ONLY_DISPLAY)}
                </strong>
              }
            />
          </Typography2>
        )}

        {isPreviewAvailable && (
          <Typography2 component="p" variant="bodyPrimary">
            {_t(
              // eslint-disable-next-line no-restricted-syntax
              "If you're ready to start learning and don't want to wait, you can preview the first module of the #{programType} for free.",
              { programType: programTypeText }
            )}
          </Typography2>
        )}
      </Content>

      <Actions>
        {isPreviewAvailable && (
          <Button
            fullWidth={isXsDown}
            variant="primary"
            css={css`
              ${isXsDown ? 'margin-bottom: var(--cds-spacing-200)' : 'margin-right: var(--cds-spacing-200)'};
            `}
            loading={isPreviewLoading}
            onClick={handlePreview}
          >
            {_t('Preview #{programType}', { programType: programTypeText })}
          </Button>
        )}
        <Button variant="secondary" fullWidth={isXsDown} onClick={handleCancelAndEnroll}>
          {_t('Cancel application and enroll')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default OpenAidApplicationModalV2;
